import firebase from './firebase';
import { user, requests, meetings, error } from './stores';
import { isUserAdmin } from './util';
import { pick } from 'lodash';
let userIsAdmin = false;
const db = firebase.firestore();
async function logEvent(action, params) {
    await db.collection('log').doc(new Date().toISOString()).set({
        action,
        user: firebase.auth().currentUser?.email,
        ...params
    });
}
export async function addRequest(meetingId, goal, seat) {
    if (await isMeetingFull(meetingId)) {
        throw new Error(`Meeting ${meetingId} is full`);
    }
    if (!(await db.collection('requests')
        .where('author', '==', firebase.auth().currentUser.email)
        .where('meeting', '==', meetingId).get()).empty) {
        throw new Error('One such request already exists');
    }
    await db.collection('requests').add({
        author: firebase.auth().currentUser.email,
        created: new Date(),
        meeting: meetingId,
        goal,
        seat: seat || null
    });
    await logEvent('addRequest', {
        meeting: meetingId,
        goal,
        seat: seat || null
    });
}
export async function acceptRequest(id, seat, goal) {
    if (!userIsAdmin)
        return;
    await db.collection('requests').doc(id).update({
        accepted: true,
        seat
    });
    const request = (await db.collection('requests').doc(id).get()).data();
    await addAttendee(request.meeting, request.author, goal || request.goal, seat);
    await logEvent('acceptRequest', { id, goal, seat });
}
export async function denyRequest(id) {
    if (!userIsAdmin)
        return;
    await db.collection('requests').doc(id).update({
        accepted: false
    });
    await logEvent('denyRequest', { id });
}
export async function removeRequest(id) {
    await db.collection('requests').doc(id).delete();
    await logEvent('removeRequest', { id });
}
export async function addMeeting(id, start, end, capacity) {
    await db.collection('meetings').doc(id).set({
        capacity,
        startTime: start,
        endTime: end,
        attendees: {}
    });
    await logEvent('addMeeting', { id, start, end, capacity });
}
const userCache = {};
export async function getUserInfo(email) {
    if (email in userCache)
        return userCache[email];
    const doc = await db.collection('members').doc(email).get();
    const member = {
        email,
        name: doc.get('name'),
        team: doc.get('team'),
        banned: doc.get('banned')
    };
    userCache[email] = member;
    return member;
}
async function parseMeetingAttendee(email, obj) {
    return {
        identity: await getUserInfo(email),
        ...obj
    };
}
export async function postAttendance(meetingId, presence) {
    const updateObject = {};
    for (const email in presence) {
        updateObject[email] = { present: presence[email] };
    }
    await db.collection('meetings').doc(meetingId).set({
        attendees: updateObject
    }, { merge: true });
    await logEvent('postAttendance', { meetingId, presence });
}
// Why not subcollections? Because that would make listening for realtime changes much more complicated.
// Each subcollection needs its own listener.
export async function addAttendee(meetingId, email, goal, seat) {
    await db.collection('meetings').doc(meetingId).set({
        attendees: {
            [email]: {
                goal,
                seat,
                present: null
            }
        }
    }, { merge: true });
    await logEvent('addAttendee', { meetingId, email, goal, seat });
}
export async function removeAttendee(meetingId, email) {
    await db.collection('meetings').doc(meetingId).set({
        attendees: {
            [email]: firebase.firestore.FieldValue.delete()
        }
    }, { merge: true });
    await logEvent('removeAttendee', { meetingId, email });
}
export async function isMeetingFull(meetingId) {
    const doc = await db.collection('meetings').doc(meetingId).get();
    if (!doc.exists)
        throw new Error('no such meeting: ' + meetingId);
    const attendees = Object.keys(doc.get('attendees')).length;
    const capacity = doc.get('capacity');
    return attendees >= capacity;
}
export async function updateMeeting(meeting) {
    await db.collection('meetings').doc(meeting.id).set({
        ...meeting,
        attendees: Object.fromEntries(meeting.attendees.map(a => [a.identity.email, pick(a, ['goal', 'present', 'seat'])]))
    });
    await logEvent('updateMeeting', { id: meeting.id });
}
async function collectQueryDocuments(query, parser) {
    const items = [];
    query.forEach(doc => items.push(parser(doc)));
    return await Promise.all(items);
}
export const hRequests = {
    reference() {
        return db.collection('requests')
            .orderBy('created', 'desc');
    },
    async parse(doc) {
        return {
            ...doc.data(),
            id: doc.id,
            author: await getUserInfo(doc.get('author')),
            created: parseFirestoreDate(doc.get('created'))
        };
    },
    store: requests
};
export const hMeetings = {
    reference() {
        const weekInMs = 1000 * 60 * 60 * 24 * 14;
        const rangeStart = new Date(Date.now() - weekInMs);
        const rangeEnd = new Date(Date.now() + weekInMs);
        return db.collection('meetings')
            .where('startTime', '>', rangeStart)
            .where('startTime', '<', rangeEnd);
    },
    async parse(doc) {
        const attendeePromises = Object.entries(doc.get('attendees')).map(([email, obj]) => parseMeetingAttendee(email, obj));
        return {
            id: doc.id,
            startTime: parseFirestoreDate(doc.get('startTime')),
            endTime: parseFirestoreDate(doc.get('endTime')),
            capacity: doc.get('capacity'),
            attendees: await Promise.all(attendeePromises)
        };
    },
    store: meetings
};
function parseFirestoreDate(date) {
    return new Date(date.seconds * 1000);
}
export async function isUserClubMember(user) {
    try {
        const doc = await db.collection('members').doc(user.email).get();
        return doc.exists;
    }
    catch (e) {
        return false;
    }
}
export async function getAllClubMembers() {
    const query = await db.collection('members').get();
    return query.docs.map(doc => doc.data());
}
let snapshotListeners = [];
user.subscribe(async (it) => {
    snapshotListeners.forEach(cancel => cancel());
    const handles = [hRequests, hMeetings];
    if (it != null) {
        if (await isUserClubMember(it)) {
            userIsAdmin = isUserAdmin(it);
            for (const handle of handles) {
                snapshotListeners.push(handle.reference().onSnapshot(async (query) => {
                    handle.store.set(await collectQueryDocuments(query, handle.parse));
                }, error.set));
            }
        }
        else {
            alert("ARC AMS is available to club members only.");
            await firebase.auth().signOut();
        }
    }
    else {
        for (const handle of handles) {
            handle.store.set([]);
        }
    }
});
export async function tryDo(fn) {
    try {
        await fn();
    }
    catch (err) {
        error.set(err);
    }
}
// Since attendees are subcollections, we need separate listeners
// const attendeeListeners = {};
// meetings.subscribe((it: Meeting[]) => {
//     const meetingIds = it.map(mtg => mtg.id);
//     const existingIds = Object.keys(attendeeListeners);
//     const newIds = meetingIds.filter(it => !existingIds.includes(it));
//     const obsoleteIds = existingIds.filter(it => !meetingIds.includes(it));
//     for (const id of obsoleteIds) {
//         attendeeListeners[id]();
//         delete attendeeListeners[id];
//     }
//     for (const id of newIds) {
//         attendeeListeners[id] = db.collection('meetings').doc(id).collection('attendees').onSnapshot(async snap => {
//             const attendees = await collectQueryDocuments(snap, parseMeetingAttendee);
//             meetings.update((mtgs: Meeting[]) => {
//                 for (const mtg of mtgs) {
//                     if (mtg.id === id) {
//                         console.log(`updating attendees of meeting ${id} to length ${attendees.length}`);
//                         mtg.attendees = attendees;
//                         break;
//                     }
//                 }
//                 return mtgs;
//             })
//         });
//     }
// });
